// src/components/DynamicMenu.tsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Menu, MenuItemLink } from 'react-admin';
import { fetchUserIdFromServer, fetchCurrentRoleIdFromServer } from '../services/auth.ts';
import { PermissionContext } from '../context/PermissionContext.tsx';
import { fetchUserPermissions, fetchPermissionsByRoleId, fetchMenusWithParents } from '../services/permissionService.ts';
import { ExpandMore, KeyboardArrowRight } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';

import WarehouseIcon from "@mui/icons-material/Warehouse";
import BuildIcon from "@mui/icons-material/Build";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CalculateIcon from "@mui/icons-material/Calculate";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GridViewIcon from "@mui/icons-material/GridView";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import MenuIcon from "@mui/icons-material/Menu";
import CategoryIcon from "@mui/icons-material/Category";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import GroupIcon from "@mui/icons-material/Group";
import FolderIcon from "@mui/icons-material/Folder";
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import WorkIcon from '@mui/icons-material/Work';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import ScienceIcon from '@mui/icons-material/Science';
interface MenuItem {
  id: number;
  parentId?: number | null;
  title: string;
  path?: string;
  icon?: string | null;
  sortOrder?: number;
  isHidden?: boolean;
  children?: MenuItem[];
}

interface MenuPermissions {
  menus: any[];
  actionIds: number[];
  actionCodes: string[];
  scopedActionCodes?: string[];
  roleId?: number;
}

export const PERMISSION_SNAPSHOT_KEY = 'permissionSnapshot';
const PERMISSION_SNAPSHOT_UPDATED_EVENT = 'permissionSnapshotUpdated';

// Icon map - use component types to avoid JSX type issues
const iconMap: Record<string, React.ComponentType> = {
  Permission: AdminPanelSettingsIcon,
  Users: PeopleIcon,
  Departments: GroupIcon,
  Roles: BadgeIcon,
  Positions: BadgeIcon, // Add position icon
  Menus: MenuIcon,
  Actions: SecurityIcon,
  Materials: GridViewIcon,
  BOM: BuildIcon,
  "Sales Order": ReceiptLongIcon,
  Inventory: WarehouseIcon,
  Supplier: BusinessIcon,
  "Purchase Plan": AssignmentIcon,
  Calculate: CalculateIcon,
  Prodoc: BusinessCenterIcon,
  Products: CategoryIcon,
  Customer: PersonIcon,
  Documents: FolderIcon,
  DocumentsList: FolderIcon,
  WorkFlow: WorkIcon,
  MyTask: AssignmentLateOutlinedIcon,
  Template: PostAddOutlinedIcon,
  DepartmentTitle: FolderIcon,
  COA: ScienceIcon,
  NCR: AssignmentLateOutlinedIcon,
  '规格数据管理': FolderIcon,
  '放行任务': AssignmentLateOutlinedIcon,
  'Specification Data': FolderIcon,
  'Release Tasks': AssignmentLateOutlinedIcon,
  'Experiment Data': ScienceIcon,
};

const ensureCoaMenus = (tree: MenuItem[]): MenuItem[] => {
  const coaPath = '/coa';
  const specPath = '/coa/spec-data';
  const experimentPath = '/coa/experiment';
  const releasePath = '/coa/release-tasks';

  const hasPath = (menus: MenuItem[], path: string): boolean =>
    menus.some((m) => {
      const currentPath = (m.path || '').startsWith('/') ? (m.path || '') : `/${m.path || ''}`;
      return currentPath === path || (m.children ? hasPath(m.children, path) : false);
    });

  const hasCoaParent = tree.some((m) => m.title === 'COA' || ((m.path || '').startsWith('/') ? m.path : `/${m.path || ''}`) === coaPath);
  const hasSpec = hasPath(tree, specPath);
  const hasExperiment = hasPath(tree, experimentPath);
  const hasRelease = hasPath(tree, releasePath);

  if (hasCoaParent && hasSpec && hasExperiment && hasRelease) return tree;

  const fallbackParentId = -900001;
  const fallbackParent: MenuItem = {
    id: fallbackParentId,
    parentId: null,
    title: 'COA',
    path: '/coa',
    icon: 'COA',
    sortOrder: 9990,
    children: [],
  };

  if (!hasSpec) {
    fallbackParent.children!.push({
      id: -900002,
      parentId: fallbackParentId,
      title: 'Specification Data',
      path: specPath,
      icon: 'Specification Data',
      sortOrder: 1,
      children: [],
    });
  }

  if (!hasExperiment) {
    fallbackParent.children!.push({
      id: -900004,
      parentId: fallbackParentId,
      title: 'Experiment Data',
      path: experimentPath,
      icon: 'Experiment Data',
      sortOrder: 2,
      children: [],
    });
  }

  if (!hasRelease) {
    fallbackParent.children!.push({
      id: -900003,
      parentId: fallbackParentId,
      title: 'Release Tasks',
      path: releasePath,
      icon: 'Release Tasks',
      sortOrder: 3,
      children: [],
    });
  }

  if (!hasCoaParent) {
    return [...tree, fallbackParent].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  return tree.map((item) => {
    if (item.title !== 'COA' && (((item.path || '').startsWith('/') ? item.path : `/${item.path || ''}`) !== coaPath)) {
      return item;
    }
    const nextChildren = [...(item.children || [])];
    if (!hasSpec) {
      nextChildren.push({
        id: -900002,
        parentId: item.id,
        title: 'Specification Data',
        path: specPath,
        icon: 'Specification Data',
        sortOrder: 1,
        children: [],
      });
    }
    if (!hasRelease) {
      nextChildren.push({
        id: -900003,
        parentId: item.id,
        title: 'Release Tasks',
        path: releasePath,
        icon: 'Release Tasks',
        sortOrder: 3,
        children: [],
      });
    }
    if (!hasExperiment) {
      nextChildren.push({
        id: -900004,
        parentId: item.id,
        title: 'Experiment Data',
        path: experimentPath,
        icon: 'Experiment Data',
        sortOrder: 2,
        children: [],
      });
    }
    return {
      ...item,
      children: nextChildren.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    };
  });
};

const ensureNcrMenus = (tree: MenuItem[]): MenuItem[] => {
  const ncrPath = '/ncr';
  const ncrMainPath = '/Ncr';
  const templatePath = '/NcrTemplate';

  const hasPath = (menus: MenuItem[], path: string): boolean =>
    menus.some((m) => {
      const currentPath = (m.path || '').startsWith('/') ? (m.path || '') : `/${m.path || ''}`;
      return currentPath === path || (m.children ? hasPath(m.children, path) : false);
    });

  const hasNcrParent = tree.some(
    (m) => m.title === 'NCR' || ((m.path || '').startsWith('/') ? m.path : `/${m.path || ''}`) === ncrPath
  );
  const hasTemplate = hasPath(tree, templatePath);
  const hasNcrMain = hasPath(tree, ncrMainPath);

  if (hasNcrParent && hasTemplate && hasNcrMain) return tree;

  const fallbackParentId = -900101;
  const fallbackParent: MenuItem = {
    id: fallbackParentId,
    parentId: null,
    title: 'NCR',
    path: '/ncr',
    icon: 'NCR',
    sortOrder: 9991,
    children: [],
  };

  if (!hasTemplate) {
    fallbackParent.children!.push({
      id: -900102,
      parentId: fallbackParentId,
      title: 'NCR Template',
      path: templatePath,
      icon: 'NCR',
      sortOrder: 1,
      children: [],
    });
  }

  if (!hasNcrMain) {
    fallbackParent.children!.push({
      id: -900103,
      parentId: fallbackParentId,
      title: 'NCR',
      path: ncrMainPath,
      icon: 'NCR',
      sortOrder: 2,
      children: [],
    });
  }

  if (!hasNcrParent) {
    return [...tree, fallbackParent].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  return tree.map((item) => {
    if (item.title !== 'NCR' && (((item.path || '').startsWith('/') ? item.path : `/${item.path || ''}`) !== ncrPath)) {
      return item;
    }
    const nextChildren = [...(item.children || [])];
    if (!hasTemplate) {
      nextChildren.push({
        id: -900102,
        parentId: item.id,
        title: 'NCR Template',
        path: templatePath,
        icon: 'NCR',
        sortOrder: 1,
        children: [],
      });
    }
    if (!hasNcrMain) {
      nextChildren.push({
        id: -900103,
        parentId: item.id,
        title: 'NCR',
        path: ncrMainPath,
        icon: 'NCR',
        sortOrder: 2,
        children: [],
      });
    }
    return {
      ...item,
      children: nextChildren.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    };
  });
};

// Cache configuration
const CACHE_KEY_PREFIX = 'dynamicMenuCache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// Generate user-specific cache key
const getCacheKey = (userId?: string | null, roleId?: string | number | null): string => {
  const token = localStorage.getItem('token') || localStorage.getItem('session') || '';
  // Use token hash, userId and roleId to build unique key
  const tokenHash = token ? token.substring(0, 20) : 'anonymous';
  return `${CACHE_KEY_PREFIX}_${tokenHash}_${userId || 'none'}_${roleId || 'none'}`;
};

// Clear all menu caches (used on logout)
const clearAllMenuCache = () => {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith(CACHE_KEY_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
};

// Export clear cache function for other modules
export { clearAllMenuCache };

// Get the first menu path (depth-first search)
// Priority: if has children -> find first child's path -> otherwise current menu path
function findFirstMenuPath(menuTree: MenuItem[]): string | null {
  for (const menu of menuTree) {
    // If it has children, prioritize finding the first child's path (ignore the current menu's path)
    if (menu.children && menu.children.length > 0) {
      const childPath = findFirstMenuPath(menu.children);
      if (childPath) {
        // eslint-disable-next-line no-console
        console.log('[DynamicMenu] Found child menu path:', childPath, 'from parent:', menu.title);
        return childPath;
      }
    }
    
    // If no children but has a path, return the current menu's path
    if (menu.path) {
      // eslint-disable-next-line no-console
      console.log('[DynamicMenu] Found first menu path:', menu.path, 'menu:', menu.title);
      return menu.path.startsWith('/') ? menu.path : `/${menu.path}`;
    }
  }
  
  // eslint-disable-next-line no-console
  console.warn('[DynamicMenu] No accessible menu path found in menu tree');
  return null;
}

// Build menu tree
function buildMenuTree(flatMenus: MenuItem[]): MenuItem[] {
  const map = new Map<number, MenuItem>();
  const roots: MenuItem[] = [];

  flatMenus.forEach(menu => {
    map.set(menu.id, { ...menu, children: [] });
  });

  flatMenus.forEach(menu => {
    const current = map.get(menu.id)!;
    if (menu.parentId != null && map.has(menu.parentId)) {
      map.get(menu.parentId)!.children!.push(current);
    } else {
      roots.push(current);
    }
  });

  return roots.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

// Get icon component
const getIcon = (title: string, icon?: string | null): React.ReactElement | undefined => {
  const IconComponent = iconMap[title] || (icon ? iconMap[icon] : null);
  return IconComponent ? React.createElement(IconComponent) : undefined;
};

// Extract common logic for loading menus - avoid code duplication
async function loadAndProcessMenuData(
  userId: string | null,
  roleId: string | number | null,
  permissionsFetcher: () => Promise<MenuPermissions>
): Promise<{
  tree: MenuItem[];
  menuIds: Array<string | number>;
  actions: string[];
  scopedActions: string[];
  expandedIds: number[];
}> {
  const perms = await permissionsFetcher() as MenuPermissions;
  const flat = perms.menus || [];
  const fullFlat = await fetchMenusWithParents(flat);
  const tree = buildMenuTree(fullFlat as MenuItem[]);
  const normalizeActionCode = (code: any): string => String(code || '').trim().toLowerCase().replace(/^\/+/, '');
  const normalizeScopedCode = (code: any): string => {
    const raw = String(code || '').trim().toLowerCase();
    const idx = raw.lastIndexOf(':');
    if (idx === -1) return normalizeActionCode(raw);
    const menuKey = raw.slice(0, idx).replace(/^\/+|\/+$/g, '');
    const actionCode = normalizeActionCode(raw.slice(idx + 1));
    return menuKey && actionCode ? `${menuKey}:${actionCode}` : '';
  };

  const actionCodes = Array.from(new Set((perms.actionCodes || []).map(normalizeActionCode).filter(Boolean)));
  const scopedActionCodes = Array.from(new Set((perms.scopedActionCodes || []).map(normalizeScopedCode).filter(Boolean)));

  // Automatically expand first-level menus
  const level1MenusWithChildren = tree
    .filter(menu => menu.children && menu.children.length > 0)
    .map(menu => menu.id);

  const expandedIds = level1MenusWithChildren;
  const menuIdsArray = fullFlat.map((m: any) => m.id);

  return {
    tree,
    menuIds: menuIdsArray,
    actions: actionCodes,
    scopedActions: scopedActionCodes,
    expandedIds,
  };
}

// Save first menu path and cache
function saveMenuDataAndPath(
  key: string,
  data: {
    tree: MenuItem[];
    menuIds: Array<string | number>;
    actions: string[];
    scopedActions: string[];
    expandedIds: number[];
  },
  userId: string | null,
  roleId: string | number | null
) {
  // Cache data
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        data,
        userId,
        roleId: Number(roleId),
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    // Cache failure does not affect functionality
  }

  // Save the first menu path for navigation use
  const firstPath = findFirstMenuPath(data.tree);
  if (firstPath) {
    localStorage.setItem('firstUserMenuPath', firstPath);
    // eslint-disable-next-line no-console
    console.log('[DynamicMenu] First user menu path:', firstPath);
  }

  try {
    localStorage.setItem(
      PERMISSION_SNAPSHOT_KEY,
      JSON.stringify({
        roleId,
        menuIds: data.menuIds,
        actions: data.actions,
        scopedActions: data.scopedActions,
        timestamp: Date.now(),
      })
    );
    window.dispatchEvent(new Event(PERMISSION_SNAPSHOT_UPDATED_EVENT));
  } catch (e) {
    // ignore storage errors
  }
}

// 菜单项组件 - 优化渲染性能
const MenuItemComponent = React.memo(({
  item,
  level,
  sidebarIsOpen,
  isExpanded,
  onToggleExpand,
}: {
  item: MenuItem;
  level: number;
  sidebarIsOpen: boolean;
  isExpanded: boolean;
  onToggleExpand: (menuId: number) => void;
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const icon = getIcon(item.title, item.icon);
  const Arrow = isExpanded ? ExpandMore : KeyboardArrowRight;

  const groupItemSx = {
    pl: sidebarIsOpen ? (level > 0 ? `${14 + 14 * level}px` : '8px') : '10px',
    pr: sidebarIsOpen ? 0.5 : 1,
    py: 0.75,
    mx: 0,
    my: 0.25,
    minHeight: level === 0 ? 42 : 38,
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: sidebarIsOpen ? 'space-between' : 'center',
    userSelect: 'none',
    color: level === 0 ? '#0f172a' : '#334155',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    transition: 'all .2s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  } as const;

  const titleSx = {
    fontSize: level === 0 ? '0.9rem' : '0.86rem',
    fontWeight: level === 0 ? 700 : 600,
    lineHeight: 1.2,
    color: 'inherit',
  } as const;

  if (item.path && !hasChildren) {
    // Case 1: has path, no children
    const toPath = item.path.startsWith('/') ? item.path : `/${item.path}`;
    return (
      <MenuItemLink
        to={toPath}
        primaryText={sidebarIsOpen ? item.title : ''}
        leftIcon={icon}
        dense={!sidebarIsOpen}
        sx={{
          ml: 0,
          mr: 0,
          my: 0.25,
          minHeight: level === 0 ? 42 : 38,
          borderRadius: '8px',
          pl: sidebarIsOpen ? (level > 0 ? `${14 + 14 * level}px` : 0.75) : 1,
          pr: 0.5,
          color: level === 0 ? '#0f172a' : '#334155',
          backgroundColor: 'transparent',
          border: '1px solid transparent',
          transition: 'all .2s ease',
          '& .RaMenuItemLink-text, & .MuiTypography-root': {
            fontSize: level === 0 ? '0.9rem' : '0.86rem',
            fontWeight: level === 0 ? 700 : 600,
            display: sidebarIsOpen ? undefined : 'none',
          },
          '& .RaMenuItemLink-icon, & .MuiSvgIcon-root': {
            color: level === 0 ? '#475569' : '#64748b',
            marginRight: sidebarIsOpen ? undefined : '0 !important',
          },
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
          '&.RaMenuItemLink-active, &[aria-current="page"]': {
            backgroundColor: '#eef2ff',
            color: '#312e81',
            '& .RaMenuItemLink-icon, & .MuiSvgIcon-root': {
              color: '#312e81',
            },
          },
        }}
      />
    );
  }

  if (hasChildren && !item.path) {
    // Case 2: no path, has children (expandable header)
    return (
      <Box
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleExpand(item.id);
        }}
        sx={groupItemSx}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {icon ? <Box sx={{ display: 'flex', color: '#64748b' }}>{icon}</Box> : null}
          {sidebarIsOpen ? (
            <Typography sx={titleSx} noWrap>
              {item.title}
            </Typography>
          ) : null}
        </Box>
        {sidebarIsOpen ? (
          <Arrow
            fontSize="small"
            sx={{
              color: '#64748b',
              opacity: level >= 1 ? 1 : 0.9,
              transition: 'transform .2s ease',
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(0deg)',
            }}
          />
        ) : null}
      </Box>
    );
  }

  if (hasChildren && item.path) {
    // Case 3: has path and has children (expandable menu group)
    return (
      <Box>
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleExpand(item.id);
          }}
          sx={groupItemSx}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {icon ? <Box sx={{ display: 'flex', color: '#64748b' }}>{icon}</Box> : null}
            {sidebarIsOpen ? (
              <Typography sx={titleSx} noWrap>
                {item.title}
              </Typography>
            ) : null}
          </Box>
          {sidebarIsOpen ? (
            <Arrow
              fontSize="small"
              sx={{
                color: '#64748b',
                opacity: level >= 1 ? 1 : 0.9,
                transition: 'transform .2s ease',
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(0deg)',
              }}
            />
          ) : null}
        </Box>
      </Box>
    );
  }

  return null;
});

// Recursively render menu tree
function renderMenuTree(
  tree: MenuItem[],
  sidebarIsOpen: boolean,
  level: number,
  expandedMenuIds: Set<number>,
  onToggleExpand: (menuId: number) => void
): React.ReactElement[] {
  return tree
    .filter(Boolean)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map(item => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedMenuIds.has(item.id);

      return (
        <React.Fragment key={item.id}>
          <MenuItemComponent
            item={item}
            level={level}
            sidebarIsOpen={sidebarIsOpen}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
          {hasChildren && isExpanded && sidebarIsOpen && item.children
            ? renderMenuTree(
                item.children,
                sidebarIsOpen,
                level + 1,
                expandedMenuIds,
                onToggleExpand
              )
            : null}
        </React.Fragment>
      );
    });
}

const DynamicMenu = ({
  sidebarIsOpen,
}: {
  sidebarIsOpen: boolean;
}) => {
  const [menuTree, setMenuTree] = useState<MenuItem[]>([]);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [menuIds, setMenuIds] = useState<Array<string | number>>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [scopedActions, setScopedActions] = useState<string[]>([]);
  const [expandedMenuIds, setExpandedMenuIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const handleToggleExpand = useCallback((menuId: number) => {
    setExpandedMenuIds(prev => {
      const next = new Set(prev);
      if (next.has(menuId)) {
        next.delete(menuId);
      } else {
        next.add(menuId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadMenuData = async () => {
      try {
        setIsLoading(true);

        // Fetch current user info
        const fetchedRoleId = await fetchCurrentRoleIdFromServer();
        const fetchedUserId = await fetchUserIdFromServer();
        const cacheKey = getCacheKey(fetchedUserId, fetchedRoleId);

        // 1. Try reading from cache
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { data, timestamp, userId, roleId: cachedRoleId } = JSON.parse(cached);
            const isValidCache = 
              Date.now() - timestamp < CACHE_DURATION &&
              (fetchedUserId ? userId === fetchedUserId : true) &&
              (fetchedRoleId ? String(cachedRoleId) === String(fetchedRoleId) : true);

            if (isValidCache) {
              const { tree, menuIds: cachedMenuIds, actions: cachedActions, scopedActions: cachedScopedActions = [], expandedIds } = data;
              if (mounted) {
                setMenuTree(tree);
                setRoleId(cachedRoleId);
                setMenuIds(cachedMenuIds);
                setActions(cachedActions);
                setScopedActions(cachedScopedActions);
                setExpandedMenuIds(new Set(expandedIds));

                try {
                  localStorage.setItem(
                    PERMISSION_SNAPSHOT_KEY,
                    JSON.stringify({
                      roleId: cachedRoleId,
                      menuIds: cachedMenuIds,
                      actions: cachedActions,
                      scopedActions: cachedScopedActions,
                      timestamp: Date.now(),
                    })
                  );
                  window.dispatchEvent(new Event(PERMISSION_SNAPSHOT_UPDATED_EVENT));
                } catch (e) {
                  // ignore storage errors
                }
                
                // Save first menu path
                const firstPath = findFirstMenuPath(tree);
                if (firstPath) {
                  localStorage.setItem('firstUserMenuPath', firstPath);
                }
                
                setIsLoading(false);
                return;
              }
            } else {
              sessionStorage.removeItem(cacheKey);
            }
          } catch (e) {
            sessionStorage.removeItem(cacheKey);
          }
        }

        // 2. Fetch menu permissions by roleId (preferred)
        if (fetchedRoleId) {
          const menuData = await loadAndProcessMenuData(
            fetchedUserId,
            fetchedRoleId,
            () => fetchPermissionsByRoleId(fetchedRoleId) as Promise<MenuPermissions>
          );

          if (!mounted) return;

          // Update state and cache
          saveMenuDataAndPath(cacheKey, menuData, fetchedUserId, fetchedRoleId);

          setMenuTree(menuData.tree);
          setRoleId(Number(fetchedRoleId));
          setMenuIds(menuData.menuIds);
          setActions(menuData.actions);
          setScopedActions(menuData.scopedActions);
          setExpandedMenuIds(new Set(menuData.expandedIds));
          setIsLoading(false);
          return;
        }

        // 3. Fallback: fetch menu permissions by userId
        if (fetchedUserId) {
          const menuData = await loadAndProcessMenuData(
            fetchedUserId,
            null,
            () => fetchUserPermissions(fetchedUserId) as Promise<MenuPermissions>
          );

          if (!mounted) return;

          saveMenuDataAndPath(cacheKey, menuData, fetchedUserId, null);

          setMenuTree(menuData.tree);
          setRoleId(null);
          setMenuIds(menuData.menuIds);
          setActions(menuData.actions);
          setScopedActions(menuData.scopedActions);
          setExpandedMenuIds(new Set(menuData.expandedIds));
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
      } catch (err) {
        console.error('DynamicMenu initialization error:', err);
        setIsLoading(false);
      }
    };

    loadMenuData();

    return () => {
      mounted = false;
    };
  }, []);

  const renderedMenuTree = useMemo(
    () => renderMenuTree(ensureNcrMenus(ensureCoaMenus(menuTree)), sidebarIsOpen, 0, expandedMenuIds, handleToggleExpand),
    [menuTree, sidebarIsOpen, expandedMenuIds, handleToggleExpand]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <PermissionContext.Provider value={{ roleId, menuIds, actions, scopedActions }}>
      <Menu
        sx={{
          width: sidebarIsOpen ? '200px' : '56px',
          minWidth: sidebarIsOpen ? '200px' : '56px',
          maxWidth: sidebarIsOpen ? '200px' : '56px',
          px: 0,
          pt: 0.5,
          pb: 1,
          '& .RaMenuItemLink-root': {
            borderRadius: '8px',
          },
          '& > *:last-child': {
            mb: '30px',
          },
        }}
      >
        {renderedMenuTree}
      </Menu>
    </PermissionContext.Provider>
  );
};

export default DynamicMenu;
